/**
 * TopCausesPanel - Top 10 root causes by volume with horizontal bar chart
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../ui/card';
import { Button } from '../../../../../ui/button';
import { Badge } from '../../../../../ui/badge';
import { Skeleton } from '../../../../../ui/skeleton';
import { Progress } from '../../../../../ui/progress';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { DeltaIndicator } from '../../../../common/components';
import type { TopCausesPanelProps } from '../../types';
import { DELTA_THRESHOLDS } from '../../constants';

export function TopCausesPanel({
  data,
  categoryFilter,
  barColor,
  onViewRecords,
  isLoading,
  isError,
  className,
}: TopCausesPanelProps) {
  // Calculate max count for proportional bar sizing
  const maxCount = useMemo(() => {
    if (!data?.causes?.length) return 0;
    return Math.max(...data.causes.map(c => c.count));
  }, [data]);

  if (isLoading) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} aria-busy="true">
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Top Root Causes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-destructive">
            Failed to load top causes data
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.causes.length === 0) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Top Root Causes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-muted-foreground">
            No root causes found for the selected filters
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid="top-causes-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Top 10 Root Causes</CardTitle>
          {categoryFilter && (
            <p className="mt-1 text-sm text-muted-foreground">
              Filtered by: <Badge variant="outline">{categoryFilter}</Badge>
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {data.causes.map((cause) => {
            const barPercentage = maxCount > 0 ? (cause.count / maxCount) * 100 : 0;

            return (
              <div
                key={cause.labelId}
                className="group flex items-center gap-3 rounded-2xl border border-transparent px-3 py-3 transition-colors hover:border-primary/15 hover:bg-accent/50 cursor-pointer"
                onClick={() => onViewRecords(cause.labelId, cause.labelName)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onViewRecords(cause.labelId, cause.labelName);
                  }
                }}
              >
                {/* Rank circle */}
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted/80">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {cause.rank}
                  </span>
                </div>

                {/* Label and bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="truncate text-sm font-medium">
                      {cause.labelName}
                    </span>
                    <Badge variant="secondary" className="flex-shrink-0 text-xs font-normal">
                      {cause.categoryName}
                    </Badge>
                  </div>
                  <Progress
                    value={barPercentage}
                    max={100}
                    className="h-2"
                    indicatorColor={barColor}
                  />
                </div>

                {/* Volume count */}
                <div className="w-16 flex-shrink-0 text-right">
                  <span className="text-sm font-semibold tabular-nums">
                    {cause.count.toLocaleString()}
                  </span>
                </div>

                {/* Delta indicator */}
                <div className="flex w-16 flex-shrink-0 items-center justify-end">
                  <DeltaIndicator
                    delta={cause.delta}
                    neutralThreshold={DELTA_THRESHOLDS.neutral}
                    isPercentage
                    size="sm"
                  />
                </div>

                {/* View button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewRecords(cause.labelId, cause.labelName);
                  }}
                  className="flex-shrink-0 text-primary opacity-50 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100"
                >
                  View
                  <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
